// Fact Blaster generator: arithmetic facts with distinct candidate answers.

import { int, pick, shuffle, type Rng } from '@kit/rng';
import type { SkillId } from '@shell/types';

export interface Fact {
  text: string;        // "6 × 7"
  answer: string;      // "42" or "3 r2"
  choices: string[];   // includes answer, all distinct
  skill: SkillId;
  hint: string;
  /** numeric answer for integer facts; quotient for remainder facts */
  value: number;
}

const CHOICES = 5;

function near(rng: Rng, answer: number, lo: number, deltas: number[]): string[] {
  const set = new Set<number>([answer]);
  for (const d of deltas) { if (set.size >= CHOICES) break; const v = answer + d; if (v >= lo && !set.has(v)) set.add(v); }
  let guard = 0;
  while (set.size < CHOICES && guard++ < 200) { const v = answer + int(rng, -12, 12); if (v >= lo && !set.has(v)) set.add(v); }
  return shuffle(rng, [...set]).map(String);
}

export function makeFact(level: number, rng: Rng): Fact {
  const L = Math.max(1, Math.min(10, level));
  if (L <= 2) {
    const add = rng() < 0.6;
    if (add) {
      const a = int(rng, 1, 10), b = int(rng, 1, 10);
      const v = a + b;
      return { text: `${a} + ${b}`, answer: String(v), choices: near(rng, v, 0, [1, -1, 2, -2, 10]), skill: 'add_sub', value: v, hint: `Start at ${Math.max(a, b)} and count on ${Math.min(a, b)}.` };
    }
    const a = int(rng, 2, 20), b = int(rng, 1, a);
    const v = a - b;
    return { text: `${a} − ${b}`, answer: String(v), choices: near(rng, v, 0, [1, -1, 2, -2, 10]), skill: 'add_sub', value: v, hint: `Count up from ${b} to ${a}. How many steps?` };
  }
  if (L <= 5) {
    const max = L === 3 ? 5 : L === 4 ? 10 : 12;
    const a = int(rng, 0, max), b = int(rng, 1, max);
    if (L === 5 && rng() < 0.4) {
      const prod = a * b;
      if (a > 0) {
        return { text: `${prod} ÷ ${b}`, answer: String(a), choices: near(rng, a, 0, [1, -1, 2, -2, b]), skill: 'mult_div', value: a, hint: `${b} times what makes ${prod}? Think of the ${b}s row.` };
      }
    }
    const v = a * b;
    const hint = a >= 6 && b >= 6 ? `Break it up: ${a} × ${b} is ${a} × 5 = ${a * 5} plus ${a} × ${b - 5} = ${a * (b - 5)}.` : `${a} × ${b} is ${a} groups of ${b}. Skip count by ${b}.`;
    return { text: `${a} × ${b}`, answer: String(v), choices: near(rng, v, 0, [a, -a, b, -b, a + b]), skill: 'mult_div', value: v, hint };
  }
  if (L <= 7) {
    const a = int(rng, 12, L === 6 ? 49 : 99), b = int(rng, 2, 9);
    if (rng() < 0.35) {
      const prod = a * b;
      return { text: `${prod} ÷ ${b}`, answer: String(a), choices: near(rng, a, 0, [1, -1, 10, -10, 2]), skill: 'mult_div', value: a, hint: `Split ${prod}: how many ${b}s in the tens, then the ones?` };
    }
    const v = a * b;
    const tens = Math.floor(a / 10) * 10, ones = a % 10;
    return { text: `${a} × ${b}`, answer: String(v), choices: near(rng, v, 0, [b, -b, 10 * b, -10 * b, a]), skill: 'mult_div', value: v, hint: `${tens} × ${b} = ${tens * b}, then ${ones} × ${b} = ${ones * b}. Add them.` };
  }
  // L8-10: mixed, including division with remainders
  const roll = rng();
  if (roll < 0.45) {
    const b = int(rng, 2, L === 8 ? 6 : 9), q = int(rng, 2, L === 8 ? 9 : 12), r = int(rng, 1, b - 1);
    const a = q * b + r;
    const answer = `${q} r${r}`;
    const set = new Set<string>([answer]);
    const alts = [`${q} r${(r + 1) % b || 1}`, `${q + 1} r${r}`, `${q - 1} r${r}`, `${q} r${Math.max(1, r - 1)}`, `${q + 1} r0`, `${q} r${b - r}`];
    for (const s of alts) { if (set.size >= CHOICES) break; if (s !== answer) set.add(s); }
    let guard = 0;
    while (set.size < CHOICES && guard++ < 50) set.add(`${int(rng, 1, 15)} r${int(rng, 0, b - 1)}`);
    return { text: `${a} ÷ ${b}`, answer, choices: shuffle(rng, [...set]), skill: 'mult_div', value: q, hint: `How many ${b}s fit in ${a}? ${q} of them make ${q * b}, and ${a - q * b} is left over.` };
  }
  if (roll < 0.75) {
    const a = int(rng, 11, 99), b = int(rng, 11, L === 8 ? 19 : 99);
    const v = a * b;
    return { text: `${a} × ${b}`, answer: String(v), choices: near(rng, v, 0, [a, -a, b, -b, 100, -100, 10, -10]), skill: 'mult_div', value: v, hint: `${a} × ${Math.floor(b / 10) * 10} = ${a * Math.floor(b / 10) * 10}, then ${a} × ${b % 10} = ${a * (b % 10)}. Add them.` };
  }
  const a = int(rng, 100, 999), b = int(rng, 100, 999);
  const add = rng() < 0.5;
  const v = add ? a + b : Math.abs(a - b);
  const [x, y] = add ? [a, b] : [Math.max(a, b), Math.min(a, b)];
  return { text: `${x} ${add ? '+' : '−'} ${y}`, answer: String(v), choices: near(rng, v, 0, [10, -10, 100, -100, 1, -1]), skill: 'add_sub', value: v, hint: add ? `Add the hundreds, then the tens, then the ones.` : `Take away the hundreds first, then the tens, then the ones.` };
}
